import { useState } from "react";
import client, { imgUrl } from "@/lib/glossyApi";
import { toast } from "sonner";
import { RefreshCw, Sparkles, Wand2, Palette, Check, Pin, X } from "lucide-react";

const SCENES = [
  { id: "product", label: "Product shot" },
  { id: "premises", label: "Premises / space" },
  { id: "service", label: "The work in action" },
  { id: "stilllife", label: "Still-life / flat-lay" },
  { id: "detail", label: "Close-up detail" },
];

const DISTRIBUTE = [
  { value: "article:spotlight", label: "Spotlight cover" },
  { value: "article:edit", label: "The Edit cover" },
  { value: "article:five_questions", label: "5 Questions cover" },
  { value: "article:blog", label: "Members' Desk cover" },
  { value: "cover_image", label: "Editorial hero cover" },
  { value: "advert_image", label: "Advert image" },
  { value: "insta_tile_image", label: "Instagram tile" },
  { value: "notice", label: "Noticeboard poster" },
];

// One controlled, on-brand BUSINESS image generator (no portraits). Reads the member's
// website to lock a house style, generates 1–4 images, then routes each anywhere.
export default function BusinessImageStudio({ member: m, setMember, articles, onArticlesChange }) {
  const [busy, setBusy] = useState("");
  const [scene, setScene] = useState("product");
  const [override, setOverride] = useState("");
  const [count, setCount] = useState(2);
  const [useRefs, setUseRefs] = useState(true);
  const [results, setResults] = useState([]);
  const style = m.brand_style || {};

  const detectStyle = async () => {
    setBusy("detect");
    try {
      const r = await client.post(`/admin/members/${m.id}/brand-style/detect`);
      setMember({ ...m, brand_style: r.data.brand_style });
      toast.success("Read their brand style from the website — tweak it below if you like.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't read their site — set the style by hand below."); }
    setBusy("");
  };

  const saveStyle = async (patch) => {
    const next = { ...style, ...patch };
    setMember({ ...m, brand_style: next });
    try { await client.put(`/admin/members/${m.id}/brand-style`, next); } catch { /* silent */ }
  };

  const setLookLock = async (payload, msg) => {
    setBusy("look");
    try {
      const r = await client.post(`/admin/members/${m.id}/look-lock`, payload);
      setMember({ ...m, look_lock: r.data.look_lock, look_lock_on: r.data.look_lock_on });
      toast.success(msg);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update the Look."); }
    setBusy("");
  };

  const generate = async () => {
    setBusy("gen");
    try {
      const r = await client.post(`/admin/members/${m.id}/business-images/generate`, {
        scene, prompt_override: override, count, use_references: useRefs,
      });
      setResults(r.data.images.map((x) => x.image));
      toast.success(`${r.data.count} on-brand image${r.data.count > 1 ? "s" : ""} ready — send each where you want it.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed — try again."); }
    setBusy("");
  };

  const distribute = async (img, target) => {
    setBusy("assign");
    try {
      if (target === "notice") {
        await client.post(`/admin/members/${m.id}/business-images/assign-notice`, { image: img });
        toast.success("Set as their Noticeboard poster.");
      } else if (target.startsWith("article:")) {
        const kind = target.split(":")[1];
        const art = (articles || {})[kind];
        if (!art) { toast.error("Nothing written for that panel yet — draft it first."); setBusy(""); return; }
        await client.patch(`/admin/articles/${art.id}`, { cover_image: img });
        toast.success(`Set as the ${DISTRIBUTE.find((d) => d.value === target)?.label}.`);
        onArticlesChange && onArticlesChange();
      } else {
        const r = await client.post(`/admin/members/${m.id}/shot-gallery/assign`, { image: img, target });
        setMember({ ...m, ...(r.data.member || {}) });
        toast.success(`Set as the ${DISTRIBUTE.find((d) => d.value === target)?.label}.`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't assign."); }
    setBusy("");
  };

  return (
    <div className="border border-ink/15 bg-white p-5 sm:p-6 space-y-6" data-testid="business-image-studio">
      <div>
        <p className="font-sans text-sm text-ink/60">
          One place to make <b className="text-ink">business</b> images — their products, premises and craft, in their own
          brand colours and style. No portraits (those live in Cover Member). Type a brief to shoot exactly what you want,
          pick how many, then send each image wherever it belongs.
        </p>
      </div>

      {/* Brand style */}
      <div className="border border-ink/10 bg-secondary/40 p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <p className="overline text-ink/60 flex items-center gap-2"><Palette size={13} /> Their house style</p>
          <button onClick={detectStyle} disabled={!!busy} className="border border-ink/40 px-4 py-2 overline text-[10px] flex items-center gap-2 hover:border-ink transition-colors disabled:opacity-60" data-testid="brand-style-detect">
            {busy === "detect" ? <RefreshCw size={12} className="animate-spin" /> : <Wand2 size={12} />} Read from their website
          </button>
        </div>
        {(style.palette || []).length > 0 && (
          <div className="flex items-center gap-2 mb-3" data-testid="brand-palette">
            {style.palette.map((c) => (
              <span key={c} className="w-6 h-6 rounded-full border border-ink/20" style={{ backgroundColor: c }} title={c} />
            ))}
            <span className="font-mono text-[10px] text-ink/45 ml-1">{style.palette.join("  ")}</span>
          </div>
        )}
        <label className="overline text-[10px] text-ink/50 block mb-1">House look (editable)</label>
        <textarea
          value={style.look || ""}
          onChange={(e) => setMember({ ...m, brand_style: { ...style, look: e.target.value } })}
          onBlur={(e) => saveStyle({ look: e.target.value })}
          rows={3}
          placeholder="Read their website above, or describe their look here — colours, lighting, mood, surfaces…"
          className="w-full border border-ink/25 p-3 font-sans text-sm focus:outline-none focus:border-gloss-gold resize-none"
          data-testid="brand-style-look"
        />
        {(style.subjects || []).length > 0 && (
          <p className="font-sans text-xs text-ink/50 mt-2">Worth shooting: {style.subjects.join(" · ")}</p>
        )}
      </div>

      {/* The Look — pinned style guide (works like Face Lock) */}
      <div className="border border-ink/10 bg-secondary/40 p-4" data-testid="look-lock-panel">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="overline text-ink/60 flex items-center gap-2"><Pin size={13} /> The Look — pinned style guide</p>
          {m.look_lock && (
            <label className="flex items-center gap-2 cursor-pointer" data-testid="look-lock-toggle">
              <input type="checkbox" checked={!!m.look_lock_on} disabled={!!busy}
                onChange={(e) => setLookLock({ on: e.target.checked }, e.target.checked ? "Look ON — every new batch matches it." : "Look OFF — batches follow the house style only.")}
                className="accent-ink" />
              <span className="font-sans text-sm text-ink/70">{m.look_lock_on ? "On — batches match this image" : "Off"}</span>
            </label>
          )}
        </div>
        {m.look_lock ? (
          <div className="flex items-center gap-4 mt-3">
            <div className={`w-24 h-24 border-2 ${m.look_lock_on ? "border-gloss-gold" : "border-ink/20 opacity-60"} overflow-hidden shrink-0`}>
              <img src={imgUrl({ cover_image: m.look_lock })} alt="The Look" className="w-full h-full object-cover" data-testid="look-lock-thumb" />
            </div>
            <div className="min-w-0">
              <p className="font-sans text-sm text-ink/70">When on, every new image copies this one's lighting, colours and mood — so a whole set looks like one shoot.</p>
              <button onClick={() => setLookLock({ image: null }, "Look cleared.")} disabled={!!busy}
                className="mt-2 overline text-[10px] border border-ink/25 text-ink/60 px-3 py-1.5 flex items-center gap-1.5 hover:border-red-600 hover:text-red-600 transition-colors" data-testid="look-lock-clear">
                <X size={11} /> Clear the Look
              </button>
            </div>
          </div>
        ) : (
          <p className="font-sans text-xs text-ink/50 mt-2">No Look pinned yet — generate below, then press “Pin as the Look” on the image you love.</p>
        )}
      </div>

      {/* Controls */}
      <div className="space-y-4">
        <div>
          <label className="overline text-[10px] text-ink/60 block mb-2">What to shoot</label>
          <div className="flex flex-wrap gap-2" data-testid="business-scene-chips">
            {SCENES.map((s) => (
              <button key={s.id} onClick={() => setScene(s.id)} disabled={!!override.trim()}
                className={`font-mono text-[11px] uppercase tracking-wider px-3 py-2 border transition-colors ${scene === s.id && !override.trim() ? "bg-ink text-cream border-ink" : "border-ink/25 hover:border-ink"} ${override.trim() ? "opacity-40" : ""}`}
                data-testid={`business-scene-${s.id}`}>{s.label}</button>
            ))}
          </div>
        </div>

        <div>
          <label className="overline text-[10px] text-ink/60 block mb-1">Your brief (overrides the buttons — shoots exactly this)</label>
          <textarea value={override} onChange={(e) => setOverride(e.target.value)} rows={2}
            placeholder="e.g. a flat-lay of their wooden toys on a deep navy surface, warm morning light, in their brand blue"
            className="w-full border border-ink/25 p-3 font-sans text-sm focus:outline-none focus:border-gloss-gold resize-none"
            data-testid="business-prompt-override" />
        </div>

        <div className="flex flex-wrap items-center gap-5">
          <div className="flex items-center gap-2">
            <span className="overline text-[10px] text-ink/60">How many</span>
            <div className="flex" data-testid="business-count">
              {[1, 2, 3, 4].map((n) => (
                <button key={n} onClick={() => setCount(n)}
                  className={`w-9 h-9 border font-mono text-sm transition-colors ${count === n ? "bg-ink text-cream border-ink" : "border-ink/25 hover:border-ink"}`}
                  data-testid={`business-count-${n}`}>{n}</button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer" data-testid="business-use-refs">
            <input type="checkbox" checked={useRefs} onChange={(e) => setUseRefs(e.target.checked)} className="accent-ink" />
            <span className="font-sans text-sm text-ink/70">Use their real product/venue photos</span>
          </label>
        </div>

        <button onClick={generate} disabled={!!busy} className="w-full bg-ink text-cream py-3.5 overline flex items-center justify-center gap-2 hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-60" data-testid="business-generate">
          {busy === "gen" ? <RefreshCw size={15} className="animate-spin" /> : <Sparkles size={15} />}
          {busy === "gen" ? "Shooting…" : `Generate ${count} image${count > 1 ? "s" : ""} (credits)`}
        </button>
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div className="border-t border-ink/10 pt-5">
          <p className="overline text-ink/60 mb-3">This shoot — send each where it belongs</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="business-results">
            {results.map((img, i) => (
              <div key={img} className="border border-ink/15" data-testid={`business-result-${i}`}>
                <div className="aspect-[4/3] bg-secondary overflow-hidden">
                  <img src={imgUrl({ cover_image: img })} alt="" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Check size={13} className="text-green-600 shrink-0" />
                    <select onChange={(e) => { if (e.target.value) { distribute(img, e.target.value); e.target.value = ""; } }}
                      disabled={!!busy} defaultValue=""
                      className="flex-1 overline text-[10px] border border-ink/30 py-2 px-2 bg-white cursor-pointer hover:border-ink transition-colors disabled:opacity-60"
                      data-testid={`business-use-in-${i}`}>
                      <option value="">Use in…</option>
                      {DISTRIBUTE.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                    </select>
                  </div>
                  <button onClick={() => setLookLock({ image: img }, "Pinned as the Look — future batches will match it.")} disabled={!!busy || m.look_lock === img}
                    className={`w-full overline text-[10px] border py-2 flex items-center justify-center gap-1.5 transition-colors ${m.look_lock === img ? "border-gloss-gold text-gloss-gold-dark" : "border-ink/25 text-ink/60 hover:border-ink"}`}
                    data-testid={`business-pin-look-${i}`}>
                    <Pin size={11} /> {m.look_lock === img ? "This is the Look" : "Pin as the Look"}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <p className="font-mono text-[10px] text-ink/40 mt-3">Every image is also saved to “AI Images Saved” in the Master section for reuse.</p>
        </div>
      )}
    </div>
  );
}
